/**
 * activityDevice.ts — which device the activity-details screen was asked for.
 *
 * ⭐ **THE ADDRESS IS THE ANSWER.** `/activity/details/:id` names the system whose insights the
 * patient asked to see — `cough` or `clear` — and that segment is the only part of the address
 * that is always present. F-142: `ActivityDetails` never read it. It read a `deviceType` QUERY
 * parameter that only `HomeDashboard.handleNavigateToDetails` appends, and fell back to `"cough"`
 * whenever that parameter was absent, so a patient who owns both systems opened
 * `/activity/details/clear` and got the Cough panel.
 *
 * ⛔ **AND THE OLD FALLBACK HID IT FROM ANYONE WITH ONE DEVICE.** `ActivityDetails` re-points
 * `activeTab` at `availableTabs[0]` when the current tab is not among the patient's devices — so a
 * Clear-only patient landed on Clear anyway and the bug was invisible. It bites exactly the
 * patient who owns both, for whom `"cough"` is always a legitimate tab and the correction never
 * fires.
 *
 * ⚠️ **IONIC KEEPS THE PAGE MOUNTED ACROSS THE TWO URLs.** `IonRouterOutlet` keys its views by
 * the MATCHED route (`/activity/details/:id`), so walking from the Cough details to the Clear
 * details re-renders the same component instance rather than mounting a new one. The old
 * `useEffect(..., [])` that read the query string therefore ran once, on the first of the two
 * screens, and never again — which is why the route segment has to be read on every render and
 * re-applied when it changes, not sampled at mount.
 *
 * ⛔ **PURE AND EXPORTED.** The screen is a 1300-line component behind an auth context, a storage
 * context and four chart libraries; the rule that decides which panel it paints should be
 * readable — and provable — without any of them.
 */

/** The two systems the app draws insights for. */
export type ActivityDevice = "cough" | "clear";

/**
 * A device name, if `value` is one — trimmed and case-folded, because a device can arrive from a
 * hand-typed address, a push payload or a restored deep link, and `/activity/details/Clear` names
 * the same screen as `/activity/details/clear`.
 *
 * ⛔ Returns `null` rather than a default for anything else. "I was not told" and "I was told
 * cough" are different states, and only the caller knows what to do with the first.
 */
export function parseDeviceParam(value: string | null | undefined): ActivityDevice | null {
  const name = (value ?? "").trim().toLowerCase();
  return name === "cough" || name === "clear" ? name : null;
}

/**
 * The device this address asks for: the `:id` route segment, or the legacy `deviceType` query
 * parameter, or `null` when neither says.
 *
 * ⭐ **THE ROUTE SEGMENT WINS.** It is the part of the address a patient can see, share and
 * bookmark, and the part every caller produces — `HomeDashboard` builds
 * `/activity/details/${deviceType}?…&deviceType=${deviceType}` and `TherapyCard` builds
 * `/activity/details/cough?profile_id=…` with no query parameter at all. Preferring the query
 * string would make the one link that carries both authoritative and the one that carries only
 * the path ignorable.
 *
 * ⚠️ **`deviceType` IS STILL READ**, second, so that an in-flight link or a stored notification
 * from before this fix keeps working. The two agree everywhere in this repo today; if they ever
 * disagree, the visible address is the one the patient asked for.
 */
export function resolveRequestedDevice(
  routeId: string | null | undefined,
  search: string | null | undefined,
): ActivityDevice | null {
  const fromRoute = parseDeviceParam(routeId);
  if (fromRoute) return fromRoute;
  const query = new URLSearchParams(search ?? "");
  return parseDeviceParam(query.get("deviceType"));
}
